import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import TransferList, { ListInfo } from '@/components/UI/TransferList/TransferList';
import { deepIsEqual } from '@/helpers/utils';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import campaignServices from '@/services/campaignServices';
import { AffectedVehiclesBySubCRResponse, CarlineInfo, SubCRAffectedVehiclesData } from '@/types/AffectedVehicles';
import { ThemeOptions } from '@/utils/componentStyles';

type InfoTransferPopUpProps = {
  subCRId: string;
  reloadSubCR: () => void;
  closePopup: () => void;
};

const useStyles = makeStyles(({ cartTheme }: ThemeOptions) => ({
  titleRoot: {
    borderBottom: `1px solid ${cartTheme.commonGrey}`,
  },
  buttonsRoot: {
    borderTop: `1px solid ${cartTheme.commonGrey}`,
  },
  dialogPaper: {
    minWidth: '30%',
  },
  dropdownOptions: {
    width: '100%',
    margin: '1rem 0',
  },
}));

const STEPS = {
  CR_SELECTION: 1,
  TRANSFER: 2,
};
const MIN_SUBCR_REQUIRED = 2;

const InfoTransferPopUp: React.FC<InfoTransferPopUpProps> = (props) => {
  const { subCRId, reloadSubCR, closePopup } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const [affectedVehiclesBySubCRInfo, setAffectedVehiclesBySubCRInfo] = useState<{ [subCRId: string]: SubCRAffectedVehiclesData }>({});
  const [carlinesInfo1, setCarlinesInfo1] = useState<ListInfo>({ listHeader: '', items: [] });
  const [carlinesInfo2, setCarlinesInfo2] = useState<ListInfo>({ listHeader: '', items: [] });
  const [countryVariantsInfo1, setCountryVariantsInfo1] = useState<ListInfo>({ listHeader: '', items: [] });
  const [countryVariantsInfo2, setCountryVariantsInfo2] = useState<ListInfo>({ listHeader: '', items: [] });
  const [subCROptions, setSubCROptions] = useState<string[]>([]);
  const [subCR1, setSubCR1] = useState<string>('');
  const [subCR2, setSubCR2] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<number>(STEPS.CR_SELECTION);
  const { addToast } = useToastContext();
  const { loading, setLoading } = useGlobalSpinnerContext();
  const isCRSelectionStep = currentStep === STEPS.CR_SELECTION;
  const isTransferStep = currentStep === STEPS.TRANSFER;

  const mergeCarlinesInfo = (carlines: CarlineInfo[]): string[] => {
    return carlines?.flatMap((carline) =>
      carline.typeVersions?.map((typeVersion) => `${carline.modelSerie}-${typeVersion}`),
    );
  };

  useEffect(() => {
    async function fetchSubCRsAffectedVehiclesData(): Promise<void> {
      let subCRsIds: string[] = [];
      setLoading(true);

      try {
        const { affectedVehiclesMapResponse }: AffectedVehiclesBySubCRResponse = await campaignServices.getAffectedVehicles(subCRId);
        subCRsIds = Object.keys(affectedVehiclesMapResponse);
        setAffectedVehiclesBySubCRInfo(affectedVehiclesMapResponse);
        setSubCROptions(subCRsIds);
      } catch (error) {
        addToast(error instanceof Error ? error.message : t('errors.errorBoundaryMessage1'), 'ERROR');
      } finally {
        setLoading(false);
      }

      if (subCRsIds.length < MIN_SUBCR_REQUIRED) {
        addToast(t('alert.minimum2SubCRsRequired'), 'Warning');
        closePopup();
      }
    }

    if (!subCRId) return;

    fetchSubCRsAffectedVehiclesData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCRId]);

  useEffect(() => {
    setSubCR2('');
    setCarlinesInfo1({
      listHeader: subCR1,
      items: mergeCarlinesInfo(affectedVehiclesBySubCRInfo?.[subCR1]?.carlines),
    });
    setCountryVariantsInfo1({
      listHeader: subCR1,
      items: affectedVehiclesBySubCRInfo?.[subCR1]?.countryVariants,
    });
  }, [subCR1, affectedVehiclesBySubCRInfo]);

  useEffect(() => {
    setCarlinesInfo2({
      listHeader: subCR2,
      items: mergeCarlinesInfo(affectedVehiclesBySubCRInfo?.[subCR2]?.carlines),
    });
    setCountryVariantsInfo2({
      listHeader: subCR2,
      items: affectedVehiclesBySubCRInfo?.[subCR2]?.countryVariants,
    });
  }, [subCR2, affectedVehiclesBySubCRInfo]);

  const formatDataToUpdate = (subCRId: string, carlinesInfo: ListInfo, countryVariantsInfo: ListInfo): { [subCRId: string]: SubCRAffectedVehiclesData } => {
    return {
      [subCRId]: {
        carlines: Object.values(carlinesInfo.items.reduce((acc, carline) => {
          const [modelSerie, typeVersion] = carline.split('-');

          if (!acc[modelSerie]) {
            acc[modelSerie] = { modelSerie, typeVersions: [] };
          }
          acc[modelSerie].typeVersions.push(typeVersion);

          return acc;
        }, {})),
        countryVariants: countryVariantsInfo.items,
      },
    };
  };

  const updateData = async (newData: { [subCRId: string]: SubCRAffectedVehiclesData }): Promise<void> => {
    try {
      await campaignServices.updateAffectedVehicles({ updateSplitRequest: newData });
      reloadSubCR();
      addToast(t('alert.changesSaved'), 'Success');
      closePopup();
    } catch (error: unknown) {
      addToast(error instanceof Error ? error.message : t('errors.errorBoundaryMessage1'), 'ERROR');
    }
  };

  const onConfirmChanges = async () => {
    const subCR1NewData = formatDataToUpdate(subCR1, carlinesInfo1, countryVariantsInfo1);
    const subCR2NewData = formatDataToUpdate(subCR2, carlinesInfo2, countryVariantsInfo2);
    const subCRsUpdatedData = {
      ...affectedVehiclesBySubCRInfo,
      ...subCR1NewData,
      ...subCR2NewData,
    };

    if (deepIsEqual(subCRsUpdatedData, affectedVehiclesBySubCRInfo)) {
      addToast(t('alert.noChangesDetected'), 'Warning');

      return;
    }
    await updateData(subCRsUpdatedData);
  };

  const showSubCROptionsDropdown = () => (
    <>
      <Dropdown
        id="subcr1-select"
        options={subCROptions}
        value={subCR1}
        handleChange={(event) => setSubCR1(event.target.value)}
        label={t('infoTransferPopUp.selectCR', { number: 1 })}
        formControlClass={classes.dropdownOptions}
        withoutTranslations
      />
      <Dropdown
        id="subcr2-select"
        options={subCROptions?.filter((option) => option !== subCR1)}
        value={subCR2}
        handleChange={(event) => setSubCR2(event.target.value)}
        disabled={!subCR1}
        label={t('infoTransferPopUp.selectCR', { number: 2 })}
        formControlClass={classes.dropdownOptions}
        withoutTranslations
      />
    </>
  );

  const showCarlinesTransferList = () => (
    <TransferList
      leftListInfo={carlinesInfo1}
      rightListInfo={carlinesInfo2}
      setLeftListInfo={setCarlinesInfo1}
      setRightListInfo={setCarlinesInfo2}
      isTransferMode
      hideTransferAllButtons
    />
  );
  const showCountryVariantsTransferList = () => (
    <TransferList
      leftListInfo={countryVariantsInfo1}
      rightListInfo={countryVariantsInfo2}
      setLeftListInfo={setCountryVariantsInfo1}
      setRightListInfo={setCountryVariantsInfo2}
      hideTransferAllButtons
      isEmptyListAllowed
    />
  );

  if (loading) return null;

  return (
    <Dialog
      open
      id="info-transfer-dialog"
      classes={{ paper: classes.dialogPaper }}
      fullWidth
      maxWidth="md"
      onClose={closePopup}
      aria-labelledby="info-transfer-dialog-title"
      data-testid="info-transfer-dialog"
    >
      <DialogTitle classes={{ root: classes.titleRoot }} id="info-transfer-dialog-title">{t('campaign.transferSubCampaignInfo')}</DialogTitle>

      <DialogContent>
        {isCRSelectionStep && showSubCROptionsDropdown()}

        {isTransferStep && (
          <>
            <AccordionPanel title={t('affectedVehicles.modelSeries')}>
              {showCarlinesTransferList()}
            </AccordionPanel>

            <AccordionPanel title={t('affectedVehicles.COUNTRY_VARIANTS')}>
              {showCountryVariantsTransferList()}
            </AccordionPanel>
          </>
        )}
      </DialogContent>

      <DialogActions classes={{ root: classes.buttonsRoot }}>
        <Button
          id="info-transfer-next-btn"
          data-testid="info-transfer-next-btn"
          disabled={!subCR2}
          variant="contained"
          color="primary"
          onClick={() => isCRSelectionStep ? setCurrentStep(STEPS.TRANSFER) : onConfirmChanges()}
        >
          {t(`dialog.${isTransferStep ? 'confirm' : 'next'}`)}
        </Button>

        <Button
          id="back-cancel-btn"
          data-testid="back-cancel-btn"
          color="primary"
          onClick={() => (isTransferStep) ? setCurrentStep(STEPS.CR_SELECTION) : closePopup()}
        >
          {t(`dialog.${(isTransferStep) ? 'back' : 'cancel'}`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InfoTransferPopUp;
